/* Every control here is associated with its label explicitly, via htmlFor + the
   control's own DOM id (Input renders `id`, Select forwards it to react-select's
   inputId). airbnb's default `assert: "both"` would additionally demand that the
   control be nested inside the <label> — which is exactly what we do not want:
   a wrapping label swallows the hint text next to the control. */
/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "htmlFor" }] */
import React, { useEffect, useRef } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ControlledInput } from 'src/components/ui/Input/Input';
import { ControlledSelect } from 'src/components/ui/Select/Select';
import {
  PERMISSION_AREA_HINT_KEYS,
  PERMISSION_AREA_KEYS,
  PERMISSION_AREA_LABEL_KEYS,
  PERMISSION_LEVEL_LABEL_KEYS,
  PermissionLevel,
  getSelectableLevels,
} from '../userWizardLogic';
import { ProfileStepValues } from '../userWizardSchema';

type Props = {
  control: Control<ProfileStepValues>;
  onGrantFullAccessToAll: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
};

const NAME_INPUT_ID = 'user-wizard-name';
const permissionSelectId = (area: string) => `user-wizard-permission-${area}`;

const UserWizardProfileStep: React.FC<Props> = ({ control, onGrantFullAccessToAll, onSubmit }) => {
  const { t } = useTranslation();
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Entering the step should put the caret where work continues, not leave focus
  // stranded on the footer button that got us here.
  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const levelOption = (level: PermissionLevel) => ({ value: level, label: t(PERMISSION_LEVEL_LABEL_KEYS[level]) });

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-32">
      <div className="flex max-w-460 flex-col gap-8">
        <label htmlFor={NAME_INPUT_ID} className="typo-text-14--bold text-full">
          {t('general.name')}
        </label>
        <ControlledInput
          ref={nameInputRef}
          id={NAME_INPUT_ID}
          control={control}
          name="name"
          placeholder={t('general.user_wizard.name_placeholder')}
          autoComplete="off"
        />
        <span className="typo-text-12 text-faded">{t('general.user_wizard.name_hint')}</span>
      </div>

      <div className="flex flex-col gap-16">
        <div className="flex flex-col gap-4">
          <h3 className="typo-text-16--bold text-full">{t('general.permissions')}</h3>
          <p className="typo-text-12 text-faded">{t('general.user_wizard.permissions_hint')}</p>
        </div>

        <div className="border-subtle rounded-base">
          {PERMISSION_AREA_KEYS.map((area, index) => (
            <div
              key={area}
              className={`grid grid-cols-1 items-center gap-16 px-16 py-12 sm:grid-cols-[1fr_22rem] ${
                index > 0 ? 'border-subtle-top' : ''
              }`}>
              <span className="flex flex-col gap-4">
                <label htmlFor={permissionSelectId(area)} className="typo-text-14--bold text-full">
                  {t(PERMISSION_AREA_LABEL_KEYS[area])}
                </label>
                <span className="typo-text-12 text-faded">{t(PERMISSION_AREA_HINT_KEYS[area])}</span>
              </span>
              <ControlledSelect<ProfileStepValues>
                id={permissionSelectId(area)}
                control={control}
                name={`permissions.${area}`}
                options={getSelectableLevels(area).map(levelOption)}
                fullWidth
              />
            </div>
          ))}
        </div>

        {/* Plain text link, same shape as the "N leere Chargen anzeigen" affordance
            in ProductInventory: a shortcut next to the list it acts on, not a
            second call to action competing with the wizard footer. */}
        <button
          type="button"
          onClick={onGrantFullAccessToAll}
          className="typo-text-12 text-color-brand-blue self-start hover:underline">
          {t('general.user_wizard.grant_full_access_to_all')}
        </button>
      </div>
    </form>
  );
};

export default UserWizardProfileStep;
